<template lang="pug">
  //- set height/aspect-ratio (.pb-ar-...) class in parent
  .carousel.relative(@click="onClick", @mousemove="onMouse", @mouseleave="onMouseLeave", :class="{'cursor-pointer': controls}")
    //- (carousel)
    template(v-if="isCarousel")
      //- slides...
      figure.carousel__slide.w-full.h-full(v-for="(image, i) in images")
        //- (slide: overflow)
        template(v-if="image._carousel_type === 'overflow'")
          .absolute.overlay.flex.overflow-y-scroll(:class="{'z-10': active === i}")
            img.m-auto.max-w-full(:src="image.url", :alt="image.caption || image.alt")
        //- (slide)
        template(v-else)
          resp-img(:bg="true", :lazy="i > 1", :bgClass="bgClass(image)", :src="image.url", :alt="image.caption || image.alt", :useHeight="image.height > image.width")
        //- (btn: open viewer)
        fullscreen-btn.invisible.md-visible.carousel__btns.trans-quick(v-if="!noFullscreen", @click="viewerAt = i", :image="image", :bgClass="bgClass(image)")
    //- (one image)
    template(v-else)
      figure.mb-12
        resp-img.block.w-full(:src="images[0].url", :alt="images[0].alt", :bg="bg ? true : false", :bgClass="bg")

    //- captions
    figcaption.left-0.w-full(v-if="!noCaptions && images.length", :class="{'absolute z-20 flex items-center justify-center': isCarousel, 'top-full md-h-53': !isViewer, 'bottom-0': isViewer}")
      span.block.mx-auto.text-center.px-15.text-13.my-15(:style="captionStyle", :class="{'line-clamp-2': isCarousel && !isViewer}", v-html="images[active].caption")

    //- arrows
    nav.hidden.md-block.carousel__btns.trans-quick(v-if="controls")
      //- btn: left
      .absolute.z-20.h-full.top-0.left-0.flex.items-center(:class="{'translx_-100': !isViewer}")
        button.pr-10.md-p-20.text-app-text.hover-text-app-text(@click.stop="next(-1)", :class="{'text-app-accent': hover === 'left'}")
          icon-arrow(style="transform:rotate(180deg)")
      //- btn: right
      .absolute.z-20.h-full.top-0.right-0.flex.items-center(:class="{'translx-100': !isViewer}")
        button.pl-10.md-p-20.text-app-text.hover-text-app-text(@click.stop="next(1)", :class="{'text-app-accent': hover === 'right'}")
          icon-arrow
    //- dots
    nav.carousel__dots.absolute.z-20.w-full.bottom-0.left-0.flex.justify-center.trans-quick.pointer-events-none(v-if="controls")
      button.w-15.px-3.hover-text-app-text.pointer-events-auto(v-for="(image, i) in images", :aria-label="`Jump to slide ${i + 1}`", :class="[active === i ? 'text-app-text' : 'text-gray-300', isViewer ? 'py-8' : 'py-6']", @click.stop="jumpTo(i)")
        span.block.h-px.w-full.bg-current

    //- (mobile fullscreen btn)
    button.md-hidden.absolute.z-20.bottom-0.right-0.p-6.text-app-text.pointer-events-auto(v-if="!noFullscreen && isCarousel", @click.stop="viewerAt = active")
      fullscreen-icon

    //- viewer
    viewer(v-if="!noFullscreen && viewerAt !== false", :images="images", :viewerAt="viewerAt", @close="viewerAt = null")
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import RespImg from '@/components/RespImg'
import IconArrow from '@/components/Icons/Arrow'
import _throttle from 'lodash/throttle'
import Viewer from './Viewer'
import FullscreenBtn from './FullscreenBtn'
import FullscreenIcon from '@/components/Icons/FullScreenIcon'
import Flickity from 'flickity'
export default {
  name: 'Carousel',
  props: ['images', 'noBgCover', 'noCaptions', 'noFullscreen', 'isViewer', 'initialIndex', 'bg', 'isArtworksGrid'],
  data () {
    return {
      flkty: null,
      hover: null,
      viewerAt: false
    }
  },
  computed: {
    ...mapState(['bus']),
    ...mapGetters(['winIs']),
    controls () {
      return this.images.length > 1
    },
    isCarousel () {
      return this.isViewer || this.winIs('md') || this.isArtworksGrid || this.images.length > 1 || this.bg
    },
    captionStyle () {
      const maxW = this.isViewer ? 810 : 400
      return {
        maxWidth: `calc(${maxW}/9 * 1em)`
      }
    },
    active () {
      return (this.flkty && this.flkty.selectedIndex) || 0
    }
  },
  methods: {
    init () {
      if (!this.isCarousel) return
      this.flkty = new Flickity(this.$el, {
        cellSelector: '.carousel__slide',
        initialIndex: this.initialIndex,
        fade: this.winIs('md'),
        draggable: !this.winIs('md') && this.images.length > 1,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: true
      })
    },
    next (incr = 1) {
      return incr < 0 ? this.flkty.previous() : this.flkty.next()
    },
    jumpTo (ii, instant = false) {
      this.flkty.select(ii, true, instant)
    },
    onMouse: _throttle(function (e) {
      this.hover = e.screenX <= window.innerWidth * 0.5 ? 'left' : 'right' // assuming overlay is full window
    }, 50),
    onMouseLeave () {
      setTimeout(() => { this.hover = null }, 60)
    },
    onClick () {
      this.next(this.hover === 'left' ? -1 : 1)
    },
    bgClass (image) {
      if (this.bg) return this.bg
      if (!image || !image.width) return ''
      return `bg-contain bg-center bg-no-repeat`
      // const bgsize = image.height / image.width <= 0.9 && !this.noBgCover ? 'bg-contain md-bg-cover' : 'bg-contain'
      // return `${bgsize} bg-top md-bg-center bg-no-repeat`
    }
  },
  watch: {
    isCarousel (crsl) {
      if (!crsl) return this.flkty && this.flkty.destroy()
      this.$nextTick(() => this.init())
    }
  },
  mounted () {
    this.init()
  },
  components: { RespImg, IconArrow, Viewer, FullscreenBtn, FullscreenIcon }
}
</script>

<style>
/* flickity viewport inherit size from carousel (set as padding-bottom from parent) */
.carousel .flickity-viewport {
  position: absolute;
  width: 100%;
  height:100% !important; /* resize() doesn't work so just force */
  top:0;
}
/* carousel fade speed (override flickity js) */
.carousel.is-fade .carousel__slide {
  opacity: 0 !important;
  transition: opacity 400ms !important;
  &.is-selected {
    opacity: 1 !important;
    transition: opacity 600ms 200ms !important;
  }
}
.carousel:focus{outline:none;}

/* non-touch styles */
@media (hover:hover) {
  /* btns invisible */
  .carousel {
    & .carousel__btns{opacity:0;}
    &:hover .carousel__btns{opacity: 1;}
  }
  /* dots on hover: scale up */
  .carousel{
    & .carousel__dots{ transform: scale(0.33, 1); }
    &:hover .carousel__dots{ transform:none;}
  }
}
</style>
